/**
 * live-badge.tsx — pulsing "Live" indicator for real-time data pages.
 *
 * Renders a small animated green dot + label pill. Matches the
 * "Live pipeline data" treatment in the Wavefunction reference screenshots
 * and the "live data" pattern from Salesforce's data-currency indicators.
 *
 * Usage:
 *   import { LiveBadge } from "@/components/ui/live-badge";
 *   <LiveBadge />                       // → "Live"
 *   <LiveBadge label="Live data" />     // → "Live data"
 *   <LiveBadge label="Scheduler on" />  // → "Scheduler on"
 *
 * The animation is defined in index.css as `outrena-live-pulse` so it
 * respects the project's CSS layer structure.
 */
import { cn } from "@/lib/utils";

interface LiveBadgeProps {
  /** Text shown after the dot. Defaults to "Live". */
  label?: string;
  className?: string;
}

export function LiveBadge({ label = "Live", className }: LiveBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5",
        "text-xs font-medium text-emerald-700",
        "dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
        className,
      )}
    >
      {/* Pulsing dot — animation class defined in index.css */}
      <span
        aria-hidden="true"
        className={cn(
          "h-1.5 w-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400",
          "outrena-live-pulse",
        )}
      />
      {label}
    </span>
  );
}
